import { useTheme } from "@emotion/react";
import { Card, Divider, Stack, Typography, Box, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PAY_DEPOSIT, PAY_FULL } from "../../store/cart/constants";
import { cartActions } from "../../store/cart/slice";

const OrderSummary = () => {
  const { registrations, products, totalDiscount, discountCode, hasClemsonCoupon } = useSelector((state) => state.cart);
  const { programType } = useSelector((state) => state.account);
  const theme = useTheme();
  const [paidInFullTotal, setPaidInFullTotal] = useState(0);
  const [depositTotal, setDepositTotal] = useState(0);
  const [productsTotal, setProductsTotal] = useState(0);
  const [remainingTotal, setRemainingTotal] = useState(0);
  const [immediateDiscounts, setImmediateDiscounts] = useState(0);
  const [remainingBalanceDiscounts, setRemainingBalanceDiscounts] = useState(0);
  const [fullPaySessions, setFullPaySessions] = useState([]);
  const [depositSessions, setDepositSessions] = useState([]);
  const dispatch = useDispatch();

  let USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  useEffect(() => {
    // Calculate all the various totals and discounts
    let fullTotal = getPaidInFullTotal();
    let depositTotal = getDepositTotal();
    let productsTotal = getProductsTotal();
    getRemainingTotal();
    calculateDiscountBreakdown();

    // Calculate the total before discounts
    const subtotal = fullTotal + depositTotal + productsTotal;

    // Set the total price in the cart store
    dispatch(cartActions.setTotalPrice(subtotal));

    // Store the immediate discount amount for the PaymentForm to use
    dispatch(cartActions.setImmediateDiscountAmount(immediateDiscounts));
  }, [registrations, hasClemsonCoupon, totalDiscount, immediateDiscounts]);

  function getPaidInFullTotal() {
    // Get sessions being paid in full
    const fullPayRegs = registrations.filter(reg =>
        reg.programType.includes(programType) && reg.payOption === PAY_FULL
    );

    setFullPaySessions(fullPayRegs);

    // Calculate total for sessions paid in full
    let total = fullPayRegs.reduce((partialSum, current) => {
      return (
          partialSum +
          current.totalCost -
          current.discounts.reduce((sum, cur) => sum + cur.amount, 0)
      );
    }, 0);

    setPaidInFullTotal(total);
    return total;
  }

  function getDepositTotal() {
    // Get sessions with deposit only payment
    const depositRegs = registrations.filter(reg =>
        reg.programType.includes(programType) && reg.payOption === PAY_DEPOSIT
    );

    setDepositSessions(depositRegs);

    // Calculate total for deposits
    let total = depositRegs.reduce((partialSum, current) => {
      return partialSum + current.depositAmount;
    }, 0);

    setDepositTotal(total);
    return total;
  }

  function getProductsTotal() {
    const ptProducts = products.filter((product) => {
      let hasProgramType = false;
      registrations.forEach((reg) => {
        if (reg.camperKey === product.camperKey) {
          if (reg.programType.includes(programType)) {
            hasProgramType = true;
          }
        }
      });
      return hasProgramType;
    });

    let total = ptProducts.reduce((partialSum, current) => {
      return partialSum + current.price;
    }, 0);

    setProductsTotal(total);
    return total;
  }

  function getRemainingTotal() {
    let total = 0;
    registrations.forEach((reg) => {
      if (
          reg.programType.includes(programType) &&
          reg.payOption === PAY_DEPOSIT
      ) {
        total += reg.totalCost - reg.depositAmount;
        // Subtract any existing discounts from the remaining balance
        total -= reg.discounts.reduce((sum, cur) => sum + cur.amount, 0);
      }
    });
    setRemainingTotal(total);
    return total;
  }

  function calculateDiscountBreakdown() {
    if (totalDiscount <= 0) {
      setImmediateDiscounts(0);
      setRemainingBalanceDiscounts(0);
      return;
    }

    let immediateDiscount = 0;
    let remainingDiscount = 0;

    if (hasClemsonCoupon) {
      // For Clemson coupon: Apply up to $200 per session, minus existing discounts
      // For sessions paid in full, discount applies immediately
      fullPaySessions.forEach(session => {
        // Calculate existing discounts for this session
        const existingDiscounts = session.discounts.reduce((sum, cur) => sum + cur.amount, 0);
        // Calculate max Clemson discount (200 - existing discounts)
        const maxDiscount = Math.min(200 - existingDiscounts, totalDiscount);
        if (maxDiscount > 0) {
          immediateDiscount += maxDiscount;
        }
      });

      // For deposit sessions, discount applies to remaining balance
      depositSessions.forEach(session => {
        // Calculate existing discounts for this session
        const existingDiscounts = session.discounts.reduce((sum, cur) => sum + cur.amount, 0);
        // Calculate max Clemson discount (200 - existing discounts)
        const maxDiscount = Math.min(200 - existingDiscounts, totalDiscount);
        if (maxDiscount > 0) {
          remainingDiscount += maxDiscount;
        }
      });
    } else {
      // For regular coupons: Apply same amount to each session
      // For sessions paid in full, discount applies immediately
      fullPaySessions.forEach(session => {
        immediateDiscount += totalDiscount;
      });

      // For deposit sessions, discount applies to remaining balance
      depositSessions.forEach(session => {
        remainingDiscount += totalDiscount;
      });
    }

    setImmediateDiscounts(immediateDiscount);
    setRemainingBalanceDiscounts(remainingDiscount);
  }

  // Calculate the final total with discount applied appropriately
  const calculateFinalTotal = () => {
    const subtotal = paidInFullTotal + depositTotal + productsTotal;
    return Math.max(0, subtotal - immediateDiscounts);
  };

  // Helper to display a tooltip with more info
  const InfoTooltip = ({ title }) => (
      <Tooltip title={title} placement="top">
        <InfoIcon fontSize="small" sx={{ marginLeft: 1, verticalAlign: 'middle', fontSize: '16px', color: 'gray' }} />
      </Tooltip>
  );

  // Get discount display text
  const getDiscountDisplayText = () => {
    if (hasClemsonCoupon) {
      return "Discount (applied now)";
    } else {
      return `Coupon (${discountCode}) applied now`;
    }
  };

  // Get tooltip for discount
  const getDiscountTooltip = () => {
    if (hasClemsonCoupon) {
      return "Applied to sessions being paid in full. Maximum $200 per session, reduced by any other applicable discounts.";
    } else {
      return "Applied to sessions being paid in full. For deposit payments, the discount will apply to your remaining balance.";
    }
  };

  // Get remaining balance discount text
  const getRemainingDiscountText = () => {
    if (hasClemsonCoupon) {
      return "Discount (applied to future balance)";
    } else {
      return `Coupon (${discountCode}) applied to future balance`;
    }
  };

  return (
      <Card sx={{ borderRadius: 0, padding: "1rem" }}>
        <Stack direction="column" spacing={1}>
          {/* Programs paid in full */}
          <div>
            <Typography variant="body" color={theme.palette.headers.main}>
              Programs paid in full
            </Typography>
            <Typography
                variant="body"
                color={theme.palette.headers.main}
                sx={{ float: "right" }}
            >
              {USDollar.format(paidInFullTotal)}
            </Typography>
          </div>

          {/* Non-refundable deposits */}
          <div>
            <Typography variant="body" color={theme.palette.headers.main}>
              Non-refundable deposits
            </Typography>
            <Typography
                variant="body"
                color={theme.palette.headers.main}
                sx={{ float: "right" }}
            >
              {USDollar.format(depositTotal)}
            </Typography>
          </div>

          {/* Additional products */}
          <div>
            <Typography variant="body" color={theme.palette.headers.main}>
              Additional products
            </Typography>
            <Typography
                variant="body"
                color={theme.palette.headers.main}
                sx={{ float: "right" }}
            >
              {USDollar.format(productsTotal)}
            </Typography>
          </div>

          {/* Discount - immediate */}
          {totalDiscount > 0 && immediateDiscounts > 0 && (
              <div>
                <Typography variant="body" color={theme.palette.headers.main}>
                  {getDiscountDisplayText()}
                  <InfoTooltip title={getDiscountTooltip()} />
                </Typography>
                <Typography
                    variant="body"
                    color="green"
                    sx={{ float: "right" }}
                >
                  -{USDollar.format(immediateDiscounts)}
                </Typography>
              </div>
          )}

          <Divider sx={{ marginTop: "8px", marginBottom: "8px" }} />

          {/* Today's total */}
          <div>
            <Typography
                variant="body"
                color={theme.palette.headers.main}
                sx={{ fontWeight: "bold" }}
            >
              Today's total
            </Typography>
            <Typography
                variant="body"
                color="#AC4E1E"
                sx={{ float: "right", fontWeight: "bold" }}
            >
              {USDollar.format(calculateFinalTotal())}
            </Typography>
          </div>

          {/* Remaining balance info */}
          {remainingTotal > 0 && (
              <Stack spacing={1} sx={{ marginTop: "10px" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Future Balance Information:
                </Typography>

                <Typography variant="caption" color="#888B8D">
                  Remaining balance: {USDollar.format(remainingTotal)}
                </Typography>

                {/* Discount - future */}
                {totalDiscount > 0 && remainingBalanceDiscounts > 0 && (
                    <Typography variant="caption" color="green">
                      {getRemainingDiscountText()}: -{USDollar.format(remainingBalanceDiscounts)}
                      <InfoTooltip title="This discount will be applied to your remaining balance due 4 weeks before camp." />
                    </Typography>
                )}

                <Typography variant="caption" color="#888B8D" sx={{ fontWeight: "bold" }}>
                  Final balance due (4 weeks before camp): {USDollar.format(Math.max(0, remainingTotal - remainingBalanceDiscounts))}
                </Typography>
              </Stack>
          )}
        </Stack>
      </Card>
  );
};

export default OrderSummary;